import React, { useState, useEffect, useRef } from "react";
import {
  X,
  Loader2,
  Plus,
  Pencil,
  Trash2,
  Mail,
  Send,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  RotateCcw,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { KpiFormulaEditor, KpiFieldMetadata, KpiFormula } from "./KpiFormulaEditor";

type KpiFieldKey = "capture_rate" | "total_calls" | "calls_answered" | "missed_calls" | "avg_talk_time";

const KPI_FIELDS: { key: KpiFieldKey; label: string }[] = [
  { key: "capture_rate",   label: "Capture Rate" },
  { key: "total_calls",    label: "Total Calls" },
  { key: "calls_answered", label: "Calls Answered" },
  { key: "missed_calls",   label: "Missed Calls" },
  { key: "avg_talk_time",  label: "Average Talk Time" },
];

// KPI v2 — core-definitions field set. Kept as its own key set (not shared with KPI_FIELDS)
// since "Missed Calls"/"Calls Answered" mean genuinely different numbers here, not just a relabel.
type KpiV2FieldKey = "capture_rate" | "total_calls_received" | "calls_answered" | "missed_calls" | "callbacks" | "voicemails" | "avg_handle_time" | "billable_minutes" | "avg_talk_time";

const KPI_V2_FIELDS: { key: KpiV2FieldKey; label: string }[] = [
  { key: "capture_rate",         label: "Capture Rate" },
  { key: "total_calls_received", label: "Total Calls Received" },
  { key: "calls_answered",       label: "Calls Answered" },
  { key: "missed_calls",         label: "Missed Calls" },
  { key: "callbacks",            label: "Callback Requests" },
  { key: "voicemails",           label: "Voicemails" },
  { key: "avg_handle_time",      label: "Average Handle Time" },
  { key: "billable_minutes",     label: "Billable Minutes" },
  { key: "avg_talk_time",        label: "Average Talk Time" },
];

type DetailedMetricKey =
  | "avg_handle_time" | "answer_rate"    | "avg_talk_time"      | "avg_speed_of_answer" | "avg_wait_time"
  | "total_calls"     | "calls_answered" | "missed_calls"       | "inbound_calls"       | "outbound_calls"
  | "abandoned_calls" | "transferred_calls" | "voicemails_received" | "capture_rate"    | "abandon_pct"
  | "transfer_pct"    | "minutes_used";

const DETAILED_METRIC_GROUPS: { label: string; keys: { key: DetailedMetricKey; label: string }[] }[] = [
  {
    label: "Performance",
    keys: [
      { key: "avg_handle_time",     label: "Average Handle Time" },
      { key: "answer_rate",         label: "Answer Rate" },
      { key: "avg_talk_time",       label: "Average Talk Time" },
      { key: "avg_speed_of_answer", label: "Avg Speed of Answer" },
      { key: "avg_wait_time",       label: "Average Wait Time" },
    ],
  },
  {
    label: "Volume",
    keys: [
      { key: "total_calls",       label: "Total Calls" },
      { key: "calls_answered",    label: "Calls Answered" },
      { key: "missed_calls",      label: "Missed Calls" },
      { key: "inbound_calls",     label: "Total Calls Inbound Received" },
      { key: "outbound_calls",    label: "Total Outbound Calls" },
      { key: "abandoned_calls",    label: "Total Volume of Abandoned Calls" },
      { key: "transferred_calls",  label: "Total Volume of Transferred Calls" },
      { key: "voicemails_received", label: "Voicemails Received" },
    ],
  },
  {
    label: "Rates & Usage",
    keys: [
      { key: "capture_rate",  label: "Capture Rate %" },
      { key: "abandon_pct",   label: "Abandon %" },
      { key: "transfer_pct",  label: "Transfer %" },
      { key: "minutes_used",  label: "Minutes Used" },
    ],
  },
];

const ALL_DETAILED_METRICS: DetailedMetricKey[] = DETAILED_METRIC_GROUPS.flatMap((g) => g.keys.map((k) => k.key));

interface EmailSubscription {
  id: string;
  reportType: "minutes_usage" | "ai_minutes_usage" | "capture_rates" | "kpi_summary" | "kpi_v2" | "detailed_performance" | "phone_log" | "inbound_call_log" | "special_report";
  frequency: "hourly" | "daily" | "weekly" | "monthly" | "per_call";
  dayOfWeek: number | null;
  dayOfMonth: number | null;
  sendTime: string;
  recipientEmail: string | null;
  isActive: boolean;
  includeCSVAttachment: boolean;
  dateRangeType: "billing_period" | "previous_week";
  kpiFields: (KpiFieldKey | KpiV2FieldKey)[] | null;
  detailedMetrics: DetailedMetricKey[] | null;
  includeDispositions: boolean;
  includeHourlyBreakdown: boolean;
  groupByWeekday: boolean;
  includeYtd: boolean;
  sendIfNoCalls: boolean;
  lastSentAt: string | null;
  nextScheduledAt: string | null;
  createdAt: string;
}

interface EmailLog {
  id: string;
  reportType: string;
  recipientEmail: string;
  status: "pending" | "sent" | "failed";
  errorMessage: string | null;
  periodStart: string;
  periodEnd: string;
  sentAt: string | null;
  createdAt: string;
}

interface EmailSubscriptionsModalProps {
  customerId: string;
  customerName: string;
  customerEmail?: string;
  serviceType?: string;
  onClose: () => void;
}

const DAYS_OF_WEEK = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

type ReportType = "minutes_usage" | "ai_minutes_usage" | "capture_rates" | "kpi_summary" | "kpi_v2" | "phone_log" | "detailed_performance" | "inbound_call_log" | "special_report";
type Frequency = "hourly" | "daily" | "weekly" | "monthly" | "per_call";
type DateRangeType = "billing_period" | "previous_week";

export function EmailSubscriptionsModal({
  customerId,
  customerName,
  customerEmail,
  serviceType,
  onClose,
}: EmailSubscriptionsModalProps) {
  const isAiCustomer = serviceType === "ai_admin" || serviceType === "ai_medical";
  const contentRef = useRef<HTMLDivElement>(null);
  const [subscriptions, setSubscriptions] = useState<EmailSubscription[]>([]);
  const [logs, setLogs] = useState<EmailLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState<EmailSubscription | null>(null);
  const [sendingTest, setSendingTest] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<"subscriptions" | "history">("subscriptions");
  const [emailServiceStatus, setEmailServiceStatus] = useState<{
    enabled: boolean;
    connected: boolean;
  } | null>(null);

  // KPI v2 formula editor state — metadata is shared across all fields/customers, fetched
  // once; formulas/overrides are specific to this customer.
  const [kpiFieldMetadata, setKpiFieldMetadata] = useState<Record<string, KpiFieldMetadata> | null>(null);
  const [kpiComponentLabels, setKpiComponentLabels] = useState<Record<string, string> | null>(null);
  const [kpiFormulas, setKpiFormulas] = useState<Record<string, KpiFormula> | null>(null);
  const [kpiOverriddenFields, setKpiOverriddenFields] = useState<Set<string>>(new Set());
  const [kpiSettingsLoading, setKpiSettingsLoading] = useState(false);
  const [expandedFormulaField, setExpandedFormulaField] = useState<string | null>(null);
  const [resettingAllFormulas, setResettingAllFormulas] = useState(false);

  const resetAllFormulasToDefault = async () => {
    setResettingAllFormulas(true);
    try {
      const res = await fetch(`/api/v1/kpi-metric-settings/${customerId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || "Failed to reset formulas");
      setKpiFormulas(json.data.formulas);
      setKpiOverriddenFields(new Set());
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to reset formulas");
    } finally {
      setResettingAllFormulas(false);
    }
  };

  // Form state for new subscription
  const [formData, setFormData] = useState({
    reportType: (isAiCustomer ? "ai_minutes_usage" : "minutes_usage") as ReportType,
    frequency: "weekly" as Frequency,
    dayOfWeek: 5,
    dayOfMonth: 1,
    sendTime: "08:00",
    recipientEmail: customerEmail || "",
    includeCSVAttachment: true,
    dateRangeType: "billing_period" as DateRangeType,
    kpiFields: ["capture_rate", "total_calls", "calls_answered", "missed_calls", "avg_talk_time"] as (KpiFieldKey | KpiV2FieldKey)[],
    detailedMetrics: ALL_DETAILED_METRICS,
    includeDispositions: true,
    includeHourlyBreakdown: true,
    groupByWeekday: true,
    includeYtd: true,
    sendIfNoCalls: false,
  });

  useEffect(() => {
    fetchData();
    checkEmailService();
  }, [customerId]);

  // Load KPI v2 formula metadata + this customer's resolved formulas the first time the
  // KPI v2 report type is selected (either on the "add subscription" form, or when editing
  // an existing kpi_v2 subscription — see the reportType effect below).
  useEffect(() => {
    if (formData.reportType !== "kpi_v2" || kpiFieldMetadata) return;
    (async () => {
      setKpiSettingsLoading(true);
      try {
        const [metaRes, settingsRes] = await Promise.all([
          fetch("/api/v1/kpi-metric-settings/metadata", {
            headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
          }),
          fetch(`/api/v1/kpi-metric-settings/${customerId}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
          }),
        ]);
        const metaJson = await metaRes.json();
        const settingsJson = await settingsRes.json();
        if (metaRes.ok && metaJson.success) {
          const byKey: Record<string, KpiFieldMetadata> = {};
          for (const f of metaJson.data.fields) byKey[f.key] = f;
          setKpiFieldMetadata(byKey);
          setKpiComponentLabels(metaJson.data.componentLabels);
        }
        if (settingsRes.ok && settingsJson.success) {
          setKpiFormulas(settingsJson.data.formulas);
          setKpiOverriddenFields(new Set(settingsJson.data.overriddenFields));
        }
      } catch (error) {
        console.error("Failed to load KPI v2 formula settings", error);
      } finally {
        setKpiSettingsLoading(false);
      }
    })();
  }, [formData.reportType, customerId, kpiFieldMetadata]);

  const checkEmailService = async () => {
    try {
      const response = await fetch("/api/v1/email-reports/status");
      if (response.ok) {
        const data = await response.json();
        setEmailServiceStatus(data.data);
      }
    } catch (error) {
      console.error("Failed to check email service status", error);
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [subsResponse, logsResponse] = await Promise.all([
        fetch(`/api/v1/email-reports/subscriptions/${customerId}`),
        fetch(`/api/v1/email-reports/logs/${customerId}`),
      ]);

      if (subsResponse.ok) {
        const subsData = await subsResponse.json();
        setSubscriptions(subsData.data || []);
      }

      if (logsResponse.ok) {
        const logsData = await logsResponse.json();
        setLogs(logsData.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch email report data", error);
      toast.error("Failed to load email report data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateSubscription = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload: any = {
        customerProfileId: customerId,
        reportType: formData.reportType,
        frequency: formData.frequency,
        sendTime: formData.sendTime,
        includeCSVAttachment: formData.includeCSVAttachment,
        dateRangeType: formData.dateRangeType,
      };

      if (formData.reportType === "kpi_summary" || formData.reportType === "kpi_v2") {
        payload.kpiFields = formData.kpiFields;
      }

      if (formData.reportType === "detailed_performance") {
        payload.detailedMetrics        = formData.detailedMetrics;
        payload.includeDispositions    = formData.includeDispositions;
        payload.includeHourlyBreakdown = formData.includeHourlyBreakdown;
        payload.groupByWeekday         = formData.groupByWeekday;
        payload.includeYtd             = formData.includeYtd;
      }
      if (formData.reportType === "phone_log" && formData.frequency === "hourly") {
        payload.sendIfNoCalls          = formData.sendIfNoCalls;
      }

      if (formData.recipientEmail) {
        payload.recipientEmail = formData.recipientEmail;
      }

      if (formData.frequency === "weekly") {
        payload.dayOfWeek = formData.dayOfWeek;
      } else if (formData.frequency === "monthly") {
        payload.dayOfMonth = formData.dayOfMonth;
      }

      const response = await fetch("/api/v1/email-reports/subscriptions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const body = await response.json();
        const msg = body.error || "";
        if (msg.toLowerCase().includes("already exists") || msg.toLowerCase().includes("duplicate")) {
          throw new Error("A subscription for this report type already exists for this customer.");
        }
        if (msg.toLowerCase().includes("recipient") || msg.toLowerCase().includes("email")) {
          throw new Error("Please check the recipient email address and try again.");
        }
        throw new Error("Failed to create subscription. Please try again.");
      }

      toast.success("Email subscription created");
      setShowAddForm(false);
      if (formData.reportType === "phone_log" && formData.frequency === "per_call") {
        await syncPerCallFlag(true);
      }
      fetchData();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to create subscription. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleActive = async (subscription: EmailSubscription) => {
    try {
      const response = await fetch(`/api/v1/email-reports/subscriptions/${subscription.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isActive: !subscription.isActive }),
      });

      if (!response.ok) {
        throw new Error("Failed to update subscription");
      }

      toast.success(subscription.isActive ? "Subscription paused" : "Subscription activated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update subscription");
    }
  };

  const handleDeleteSubscription = async (subscriptionId: string) => {
    if (!confirm("Are you sure you want to delete this subscription?")) {
      return;
    }

    try {
      const response = await fetch(`/api/v1/email-reports/subscriptions/${subscriptionId}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error("Failed to delete subscription");
      }

      toast.success("Subscription deleted");
      // If the deleted subscription was a per-call phone log, clear the flag
      const deleted = subscriptions.find(s => s.id === subscriptionId);
      if (deleted?.reportType === "phone_log" && deleted?.frequency === "per_call") {
        await syncPerCallFlag(false);
      }
      fetchData();
    } catch (error) {
      toast.error("Failed to delete subscription");
    }
  };

  const openEdit = (sub: EmailSubscription) => {
    setFormData({
      reportType:           sub.reportType as ReportType,
      frequency:            sub.frequency as Frequency,
      dayOfWeek:            sub.dayOfWeek ?? 5,
      dayOfMonth:           sub.dayOfMonth ?? 1,
      sendTime:             sub.sendTime ?? "08:00",
      recipientEmail:       sub.recipientEmail || customerEmail || "",
      includeCSVAttachment: sub.includeCSVAttachment ?? true,
      dateRangeType:        (sub.dateRangeType ?? "billing_period") as DateRangeType,
      kpiFields:            (sub.kpiFields as (KpiFieldKey | KpiV2FieldKey)[]) ?? [],
      detailedMetrics:      (sub.detailedMetrics as DetailedMetricKey[]) ?? ALL_DETAILED_METRICS,
      includeDispositions:  sub.includeDispositions ?? true,
      includeHourlyBreakdown: sub.includeHourlyBreakdown ?? true,
      groupByWeekday:       sub.groupByWeekday ?? true,
      includeYtd:           sub.includeYtd ?? true,
      sendIfNoCalls:        sub.sendIfNoCalls ?? false,
    });
    setEditingSubscription(sub);
    setShowAddForm(true);
    setTimeout(() => contentRef.current?.scrollTo({ top: 0, behavior: "smooth" }), 50);
  };

  const handleUpdate = async () => {
    if (!editingSubscription) return;
    setIsSubmitting(true);
    try {
      const payload: Record<string, any> = {
        frequency:            formData.frequency,
        dayOfWeek:            formData.frequency === "weekly"  ? formData.dayOfWeek  : null,
        dayOfMonth:           formData.frequency === "monthly" ? formData.dayOfMonth : null,
        sendTime:             formData.sendTime,
        recipientEmail:       formData.recipientEmail || null,
        includeCSVAttachment: formData.includeCSVAttachment,
        dateRangeType:        formData.dateRangeType,
      };
      if (editingSubscription.reportType === "kpi_summary" || editingSubscription.reportType === "kpi_v2") {
        payload.kpiFields = formData.kpiFields;
      }
      if (editingSubscription.reportType === "detailed_performance") {
        payload.detailedMetrics        = formData.detailedMetrics;
        payload.includeDispositions    = formData.includeDispositions;
        payload.includeHourlyBreakdown = formData.includeHourlyBreakdown;
        payload.groupByWeekday         = formData.groupByWeekday;
        payload.includeYtd             = formData.includeYtd;
      }
      if (formData.reportType === "phone_log" && formData.frequency === "hourly") {
        payload.sendIfNoCalls          = formData.sendIfNoCalls;
      }
      const res = await fetch(`/api/v1/email-reports/subscriptions/${editingSubscription.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Failed to update subscription");
      toast.success("Subscription updated");
      setShowAddForm(false);
      setEditingSubscription(null);
      if (editingSubscription.reportType === "phone_log") {
        await syncPerCallFlag(formData.frequency === "per_call");
      }
      fetchData();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to update subscription");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendTest = async (subscriptionId: string) => {
    setSendingTest(subscriptionId);
    try {
      const response = await fetch(`/api/v1/email-reports/send-test/${subscriptionId}`, {
        method: "POST",
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to send test email");
      }

      toast.success("Test email sent successfully");
      fetchData();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to send test email");
    } finally {
      setSendingTest(null);
    }
  };

  const formatLogPeriod = (log: EmailLog) => {
    const s = new Date(log.periodStart);
    const e = new Date(log.periodEnd);
    const isPerCall =
      log.reportType === "phone_log" &&
      Math.abs(s.getTime() - e.getTime()) < 60_000;

    if (isPerCall) {
      return `Per Call · ${s.toLocaleString([], {
        month: "numeric",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })}`;
    }

    if (log.reportType === "phone_log") {
      const sameDay = s.toLocaleDateString() === e.toLocaleDateString();
      return sameDay
        ? `${s.toLocaleDateString()} ${s.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} – ${e.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
        : `${s.toLocaleString([], { month: "numeric", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })} – ${e.toLocaleString([], { month: "numeric", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })}`;
    }

    return `${s.toLocaleDateString()} - ${e.toLocaleDateString()}`;
  };

  const formatReportTypeLabel = (reportType: string) => {
    switch (reportType) {
      case "minutes_usage": return "Minutes Usage";
      case "ai_minutes_usage": return "AI Minutes Usage";
      case "kpi_summary": return "KPI Summary";
      case "kpi_v2": return "KPI Summary (v2)";
      case "detailed_performance": return "Detailed Performance";
      case "phone_log": return "Phone Log";
      case "capture_rates": return "Capture Rates";
      case "inbound_call_log": return "Inbound + Outbound Call Log";
      case "special_report": return "Special Report";
      default: return reportType;
    }
  };

  const formatFrequency = (sub: EmailSubscription) => {
    switch (sub.frequency) {
      case "per_call":
        return "Per Call (after every call)";
      case "hourly":
        return "Hourly (previous hour)";
      case "daily":
        return "Daily";
      case "weekly":
        return `Weekly on ${DAYS_OF_WEEK.find((d) => d.value === sub.dayOfWeek)?.label || "Monday"}`;
      case "monthly":
        return `Monthly on day ${sub.dayOfMonth || 1}`;
      default:
        return sub.frequency;
    }
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString();
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "sent":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "failed":
        return <XCircle className="h-4 w-4 text-red-500" />;
      case "pending":
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      default:
        return null;
    }
  };

  const syncPerCallFlag = async (hasPerCallSub: boolean) => {
    const response = await fetch(`/api/v1/customer-profiles/${customerId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ perCallReport: hasPerCallSub }),
    });
    if (!response.ok) {
      throw new Error("Failed to sync per-call setting on customer profile");
    }
  };

  const handleReportTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newType = e.target.value as ReportType;
    // Field keys aren't shared between KPI and KPI v2 — reset to that type's own default set
    // so switching doesn't leave every checkbox unchecked.
    const kpiFields =
      newType === "kpi_v2"
        ? (["capture_rate", "total_calls_received", "calls_answered", "missed_calls", "callbacks", "avg_handle_time"] as (KpiFieldKey | KpiV2FieldKey)[])
        : newType === "kpi_summary"
        ? (["capture_rate", "total_calls", "calls_answered", "missed_calls", "avg_talk_time"] as (KpiFieldKey | KpiV2FieldKey)[])
        : formData.kpiFields;
    if (newType === "phone_log") {
      setFormData({ ...formData, reportType: newType, frequency: "per_call", dateRangeType: "billing_period", kpiFields });
    } else {
      // If switching away from phone_log, reset hourly/per_call to daily
      const freq = (formData.frequency === "hourly" || formData.frequency === "per_call") ? "daily" : formData.frequency;
      setFormData({ ...formData, reportType: newType, frequency: freq, kpiFields });
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Email Report Subscriptions</h2>
            <p className="text-sm text-gray-500">{customerName}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Email Service Status */}
        {emailServiceStatus && !emailServiceStatus.enabled && (
          <div className="px-6 py-3 bg-yellow-50 border-b border-yellow-100">
            <div className="flex items-center gap-2 text-yellow-800">
              <AlertCircle className="h-5 w-5" />
              <span className="text-sm">Email service is not configured. Contact administrator to enable email reports.</span>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="px-6 border-b border-gray-200">
          <nav className="flex gap-4">
            <button
              onClick={() => setActiveTab("subscriptions")}
              className={`py-3 px-1 border-b-2 text-sm font-medium ${
                activeTab === "subscriptions"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Subscriptions
            </button>
            <button
              onClick={() => setActiveTab("history")}
              className={`py-3 px-1 border-b-2 text-sm font-medium ${
                activeTab === "history"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Report History
            </button>
          </nav>
        </div>

        {/* Content */}
        <div ref={contentRef} className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : activeTab === "subscriptions" ? (
            <div className="space-y-4">
              {/* Add Subscription Button */}
              {!showAddForm && (
                <button
                  onClick={() => { setEditingSubscription(null); setShowAddForm(true); }}
                  disabled={!emailServiceStatus?.enabled}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="h-4 w-4" />
                  Add Subscription
                </button>
              )}

              {/* Add / Edit Subscription Form */}
              {showAddForm && (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h3 className="text-sm font-medium text-gray-900 mb-4">
                    {editingSubscription ? "Edit Subscription" : "New Email Subscription"}
                  </h3>
                  <form onSubmit={editingSubscription ? (e) => { e.preventDefault(); handleUpdate(); } : handleCreateSubscription} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Report Type</label>
                        <select
                          value={formData.reportType}
                          onChange={handleReportTypeChange}
                          disabled={!!editingSubscription}
                          className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        >
                          {isAiCustomer
                            ? <option value="ai_minutes_usage">AI Minutes Usage</option>
                            : <option value="minutes_usage">Minutes Usage</option>
                          }
                          {!isAiCustomer && <option value="kpi_summary">KPI Summary</option>}
                          {!isAiCustomer && <option value="kpi_v2">KPI Summary (v2)</option>}
                          {!isAiCustomer && <option value="detailed_performance">Detailed Performance Report</option>}
                          <option value="phone_log">Phone Log</option>
                          <option value="inbound_call_log">Inbound + Outbound Call Log (Inbound: Answered + Missed + VM + Abandoned; Outbound: Connected + Abandoned — with ANI)</option>
                          <option value="special_report">Special Report (Date/Time, ANI, Agent, Time, Reference#)</option>
                          {!isAiCustomer && <option value="capture_rates" disabled>Capture Rates (Coming Soon)</option>}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
                        <select
                          value={formData.frequency}
                          onChange={(e) => setFormData({ ...formData, frequency: e.target.value as Frequency })}
                          className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        >
                          {formData.reportType === "phone_log" && (
                            <option value="per_call">Per Call (after every call)</option>
                          )}
                          {formData.reportType === "phone_log" && (
                            <option value="hourly">Hourly (previous hour)</option>
                          )}
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                        </select>
                      </div>

                      {formData.reportType !== "detailed_performance" && formData.reportType !== "phone_log" && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
                          <select
                            value={formData.dateRangeType}
                            onChange={(e) => setFormData({ ...formData, dateRangeType: e.target.value as any })}
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          >
                            <option value="billing_period">Billing Period (default)</option>
                            <option value="previous_week">Previous Week (Mon–Sun)</option>
                          </select>
                        </div>
                      )}

                      {formData.frequency === "weekly" && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Day of Week</label>
                          <select
                            value={formData.dayOfWeek}
                            onChange={(e) => setFormData({ ...formData, dayOfWeek: Number(e.target.value) })}
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          >
                            {DAYS_OF_WEEK.map((day) => (
                              <option key={day.value} value={day.value}>
                                {day.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {formData.frequency === "monthly" && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Day of Month</label>
                          <select
                            value={formData.dayOfMonth}
                            onChange={(e) => setFormData({ ...formData, dayOfMonth: Number(e.target.value) })}
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          >
                            {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                              <option key={day} value={day}>
                                {day}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {formData.frequency !== "hourly" && formData.frequency !== "per_call" && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Send Time</label>
                          <input
                            type="time"
                            value={formData.sendTime}
                            onChange={(e) => setFormData({ ...formData, sendTime: e.target.value })}
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Recipient Email <span className="text-gray-400">(optional)</span>
                        </label>
                        <input
                          type="email"
                          value={formData.recipientEmail}
                          onChange={(e) => setFormData({ ...formData, recipientEmail: e.target.value })}
                          placeholder={customerEmail || "Uses customer email"}
                          className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>

                      {formData.reportType !== "kpi_summary" && formData.reportType !== "kpi_v2" && formData.reportType !== "detailed_performance" && formData.reportType !== "phone_log" && formData.reportType !== "inbound_call_log" && formData.reportType !== "special_report" && (
                        <div className="col-span-2">
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={formData.includeCSVAttachment}
                              onChange={(e) => setFormData({ ...formData, includeCSVAttachment: e.target.checked })}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">Include CSV attachment</span>
                          </label>
                        </div>
                      )}

                      {formData.reportType === "kpi_summary" && (
                        <div className="col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-2">KPI Fields to Include</label>
                          <div className="space-y-2 bg-white border border-gray-200 rounded-md p-3">
                            {KPI_FIELDS.map((field) => (
                              <label key={field.key} className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={formData.kpiFields.includes(field.key)}
                                  onChange={(e) => {
                                    const updated = e.target.checked
                                      ? [...formData.kpiFields, field.key]
                                      : formData.kpiFields.filter((k) => k !== field.key);
                                    setFormData({ ...formData, kpiFields: updated });
                                  }}
                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-700">{field.label}</span>
                              </label>
                            ))}
                          </div>
                          {formData.kpiFields.length === 0 && (
                            <p className="text-xs text-red-500 mt-1">At least one KPI field must be selected.</p>
                          )}
                        </div>
                      )}

                      {formData.reportType === "kpi_v2" && (
                        <div className="col-span-2">
                          <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-medium text-gray-700">KPI v2 Fields to Include</label>
                            {kpiOverriddenFields.size > 0 && (
                              <button
                                type="button"
                                onClick={resetAllFormulasToDefault}
                                disabled={resettingAllFormulas}
                                className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
                              >
                                {resettingAllFormulas ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
                                Reset all to default
                              </button>
                            )}
                          </div>
                          <p className="text-xs text-gray-400 mb-2">
                            Check a field to include it in the report. Click "Formula" to see (and optionally change) exactly how that field is calculated for this customer.
                          </p>
                          <div className="space-y-1 bg-white border border-gray-200 rounded-md p-3">
                            {KPI_V2_FIELDS.map((field) => {
                              const isChecked = formData.kpiFields.includes(field.key);
                              const isExpanded = expandedFormulaField === field.key;
                              const fieldMeta = kpiFieldMetadata?.[field.key];
                              const fieldFormula = kpiFormulas?.[field.key];
                              return (
                                <div key={field.key}>
                                  <div className="flex items-center gap-2">
                                    <label className="flex items-center gap-2 cursor-pointer flex-1">
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={(e) => {
                                          const updated = e.target.checked
                                            ? [...formData.kpiFields, field.key]
                                            : formData.kpiFields.filter((k) => k !== field.key);
                                          setFormData({ ...formData, kpiFields: updated });
                                        }}
                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                      />
                                      <span className="text-sm text-gray-700">{field.label}</span>
                                      {kpiOverriddenFields.has(field.key) && (
                                        <span className="text-[10px] text-amber-600 font-medium">customized</span>
                                      )}
                                    </label>
                                    {isChecked && (
                                      <button
                                        type="button"
                                        onClick={() => setExpandedFormulaField(isExpanded ? null : field.key)}
                                        className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-0.5"
                                      >
                                        {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                                        Formula
                                      </button>
                                    )}
                                  </div>
                                  {isChecked && isExpanded && (
                                    kpiSettingsLoading || !fieldMeta || !fieldFormula || !kpiComponentLabels ? (
                                      <p className="text-xs text-gray-400 mt-1 ml-6">Loading formula...</p>
                                    ) : (
                                      <KpiFormulaEditor
                                        customerId={customerId}
                                        metadata={fieldMeta}
                                        componentLabels={kpiComponentLabels}
                                        formula={fieldFormula}
                                        isOverridden={kpiOverriddenFields.has(field.key)}
                                        onSaved={(formula) => {
                                          setKpiFormulas((prev) => ({ ...(prev ?? {}), [field.key]: formula }));
                                          setKpiOverriddenFields((prev) => new Set(prev).add(field.key));
                                        }}
                                        onReset={() => {
                                          if (fieldMeta) {
                                            setKpiFormulas((prev) => ({ ...(prev ?? {}), [field.key]: fieldMeta.defaultFormula }));
                                          }
                                          setKpiOverriddenFields((prev) => {
                                            const next = new Set(prev);
                                            next.delete(field.key);
                                            return next;
                                          });
                                        }}
                                      />
                                    )
                                  )}
                                </div>
                              );
                            })}
                          </div>
                          {formData.kpiFields.length === 0 && (
                            <p className="text-xs text-red-500 mt-1">At least one KPI field must be selected.</p>
                          )}
                        </div>
                      )}

                      {formData.reportType === "detailed_performance" && (
                        <div className="col-span-2 space-y-4">
                          {/* Billing period notice */}
                          <p className="text-xs text-blue-600 bg-blue-50 border border-blue-100 rounded px-3 py-2">
                            Date range is always <strong>Billing Period</strong> for this report type.
                          </p>

                          {/* Metrics picker */}
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <label className="block text-sm font-medium text-gray-700">Metrics to Include</label>
                              <div className="flex gap-3 text-xs">
                                <button
                                  type="button"
                                  onClick={() => setFormData({ ...formData, detailedMetrics: ALL_DETAILED_METRICS })}
                                  className="text-blue-600 hover:underline"
                                >
                                  Select all
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setFormData({ ...formData, detailedMetrics: [] })}
                                  className="text-gray-500 hover:underline"
                                >
                                  Clear all
                                </button>
                              </div>
                            </div>
                            <div className="bg-white border border-gray-200 rounded-md p-3 space-y-4">
                              {DETAILED_METRIC_GROUPS.map((group) => (
                                <div key={group.label}>
                                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{group.label}</p>
                                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                                    {group.keys.map((field) => (
                                      <label key={field.key} className="flex items-center gap-2 cursor-pointer">
                                        <input
                                          type="checkbox"
                                          checked={formData.detailedMetrics.includes(field.key)}
                                          onChange={(e) => {
                                            const updated = e.target.checked
                                              ? [...formData.detailedMetrics, field.key]
                                              : formData.detailedMetrics.filter((k) => k !== field.key);
                                            setFormData({ ...formData, detailedMetrics: updated });
                                          }}
                                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        />
                                        <span className="text-sm text-gray-700">{field.label}</span>
                                      </label>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                            {formData.detailedMetrics.length === 0 && (
                              <p className="text-xs text-red-500 mt-1">At least one metric must be selected.</p>
                            )}
                          </div>

                          {/* Section toggles */}
                          <div className="bg-white border border-gray-200 rounded-md p-3 space-y-2">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Report Sections</p>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.includeDispositions}
                                onChange={(e) => setFormData({ ...formData, includeDispositions: e.target.checked })}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-700">Include Dispositions <span className="text-gray-400">(tag name · calls · % of total · AHT)</span></span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.includeHourlyBreakdown}
                                onChange={(e) => setFormData({ ...formData, includeHourlyBreakdown: e.target.checked })}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-700">Include Hourly Breakdown <span className="text-gray-400">(calls · avg mins · calls &gt;5 min per hour)</span></span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.groupByWeekday}
                                onChange={(e) => setFormData({ ...formData, groupByWeekday: e.target.checked })}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-700">Group by Weekday / Weekend <span className="text-gray-400">(separate sheets: Week 1, Week 2… / Weekend 1, Weekend 2…)</span></span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.includeYtd}
                                onChange={(e) => setFormData({ ...formData, includeYtd: e.target.checked })}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-700">Include YTD Report <span className="text-gray-400">(year-to-date monthly breakdown sheet)</span></span>
                            </label>
                          </div>
                        </div>
                      )}

                      {/* Per-call info banner */}
                      {formData.reportType === "phone_log" && formData.frequency === "per_call" && (
                        <div className="border border-teal-200 bg-teal-50 rounded-lg p-3">
                          <p className="text-sm text-teal-800 font-medium">Per Call Mode</p>
                          <p className="text-xs text-teal-600 mt-1">
                            A phone log PDF will be emailed automatically within ~2 minutes of each call to the recipient address(es) on your per-call subscription(s). Add multiple subscriptions to send to multiple addresses.
                          </p>
                        </div>
                      )}

                      {/* Send-if-no-calls toggle — only for Phone Log hourly reports */}
                      {formData.reportType === "phone_log" && formData.frequency === "hourly" && (
                        <div className="border border-amber-200 bg-amber-50 rounded-lg p-3">
                          <label className="flex items-start gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.sendIfNoCalls}
                              onChange={(e) => setFormData({ ...formData, sendIfNoCalls: e.target.checked })}
                              className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">
                              Send report even when no calls received
                              <span className="block text-xs text-gray-400 mt-0.5">
                                By default, hourly reports are suppressed until the first call of the day is received. Enable this to always send, even with zero calls.
                              </span>
                            </span>
                          </label>
                        </div>
                      )}
                    </div>

                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => { setShowAddForm(false); setEditingSubscription(null); }}
                        disabled={isSubmitting}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={
                          isSubmitting ||
                          ((formData.reportType === "kpi_summary" || formData.reportType === "kpi_v2") && formData.kpiFields.length === 0) ||
                          (formData.reportType === "detailed_performance" && formData.detailedMetrics.length === 0)
                        }
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmitting && (
                          <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                        )}
                        {isSubmitting
                          ? (editingSubscription ? "Saving..." : "Creating...")
                          : (editingSubscription ? "Save Changes" : "Create Subscription")
                        }
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Subscriptions List */}
              {subscriptions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Mail className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No email subscriptions configured</p>
                  <p className="text-sm">Add a subscription to send automated reports to this customer</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {subscriptions.map((sub) => (
                    <div
                      key={sub.id}
                      className={`border rounded-lg p-4 ${sub.isActive ? "bg-white" : "bg-gray-50"}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">
                              {sub.reportType === "minutes_usage"
                                ? "Minutes Usage Report"
                                : sub.reportType === "ai_minutes_usage"
                                ? "AI Minutes Usage Report"
                                : sub.reportType === "kpi_summary"
                                ? "KPI Summary Report"
                                : sub.reportType === "detailed_performance"
                                ? "Detailed Performance Report"
                                : sub.reportType === "phone_log"
                                ? "Phone Log Report"
                                : sub.reportType === "inbound_call_log"
                                ? "Inbound + Outbound Call Log Report"
                                : sub.reportType === "special_report"
                                ? "Special Report"
                                : sub.reportType === "capture_rates"
                                ? "Capture Rates Report"
                                : formatReportTypeLabel(sub.reportType)}
                            </span>
                            <span
                              className={`px-2 py-0.5 text-xs rounded-full ${
                                sub.isActive
                                  ? "bg-green-100 text-green-700"
                                  : "bg-gray-100 text-gray-600"
                              }`}
                            >
                              {sub.isActive ? "Active" : "Paused"}
                            </span>
                          </div>
                          <div className="mt-1 text-sm text-gray-500 space-y-1">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4" />
                              <span>{formatFrequency(sub)}</span>
                              {sub.dateRangeType === "previous_week" && (
                                <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                                  Prev Week
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4" />
                              <span>at {sub.sendTime}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Mail className="h-4 w-4" />
                              <span>{sub.recipientEmail || customerEmail || "No email"}</span>
                            </div>
                            {sub.nextScheduledAt && sub.isActive && (
                              <div className="text-xs text-blue-600">
                                Next: {formatDateTime(sub.nextScheduledAt)}
                              </div>
                            )}
                            {sub.reportType === "kpi_summary" && sub.kpiFields && sub.kpiFields.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {sub.kpiFields.map((k) => (
                                  <span key={k} className="px-1.5 py-0.5 text-xs bg-indigo-50 text-indigo-700 rounded border border-indigo-200">
                                    {KPI_FIELDS.find((f) => f.key === k)?.label ?? k}
                                  </span>
                                ))}
                              </div>
                            )}
                            {sub.reportType === "detailed_performance" && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {sub.detailedMetrics && sub.detailedMetrics.length > 0 && (
                                  <span className="px-1.5 py-0.5 text-xs bg-purple-50 text-purple-700 rounded border border-purple-200">
                                    {sub.detailedMetrics.length} metrics selected
                                  </span>
                                )}
                                {sub.includeDispositions && (
                                  <span className="px-1.5 py-0.5 text-xs bg-purple-50 text-purple-700 rounded border border-purple-200">Dispositions</span>
                                )}
                                {sub.includeHourlyBreakdown && (
                                  <span className="px-1.5 py-0.5 text-xs bg-purple-50 text-purple-700 rounded border border-purple-200">Hourly</span>
                                )}
                                {sub.groupByWeekday && (
                                  <span className="px-1.5 py-0.5 text-xs bg-purple-50 text-purple-700 rounded border border-purple-200">Weekday/Weekend split</span>
                                )}
                                {sub.includeYtd && (
                                  <span className="px-1.5 py-0.5 text-xs bg-purple-50 text-purple-700 rounded border border-purple-200">YTD</span>
                                )}
                              </div>
                            )}
                            {sub.sendIfNoCalls && (
                              <span className="px-1.5 py-0.5 text-xs bg-amber-50 text-amber-700 rounded border border-amber-200">Always send</span>
                            )}
                            {sub.lastSentAt && (
                              <div className="text-xs text-gray-400">
                                Last sent: {formatDateTime(sub.lastSentAt)}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openEdit(sub)}
                            className="p-2 text-gray-500 hover:bg-gray-100 rounded-md"
                            title="Edit Subscription"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleSendTest(sub.id)}
                            disabled={sendingTest === sub.id}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-md"
                            title="Send Test Email"
                          >
                            {sendingTest === sub.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Send className="h-4 w-4" />
                            )}
                          </button>
                          <button
                            onClick={() => handleToggleActive(sub)}
                            className={`p-2 rounded-md ${
                              sub.isActive
                                ? "text-yellow-600 hover:bg-yellow-50"
                                : "text-green-600 hover:bg-green-50"
                            }`}
                            title={sub.isActive ? "Pause" : "Activate"}
                          >
                            {sub.isActive ? (
                              <AlertCircle className="h-4 w-4" />
                            ) : (
                              <CheckCircle className="h-4 w-4" />
                            )}
                          </button>
                          <button
                            onClick={() => handleDeleteSubscription(sub.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            /* History Tab */
            <div>
              {logs.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Mail className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No report history yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {logs.map((log) => (
                    <div
                      key={log.id}
                      className="border border-gray-200 rounded-lg p-4 bg-white"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                          {getStatusIcon(log.status)}
                          <span
                            className={`text-sm font-medium capitalize ${
                              log.status === "sent"
                                ? "text-green-600"
                                : log.status === "failed"
                                ? "text-red-600"
                                : "text-yellow-600"
                            }`}
                          >
                            {log.status}
                          </span>
                          <span className="text-sm text-gray-500">·</span>
                          <span className="text-sm text-gray-900 truncate">
                            {formatReportTypeLabel(log.reportType)}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500 whitespace-nowrap shrink-0">
                          {formatDateTime(log.sentAt || log.createdAt)}
                        </span>
                      </div>
                      <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-gray-500">Recipient: </span>
                          <span className="text-gray-900 break-all">
                            {log.recipientEmail || "—"}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">Period: </span>
                          <span className="text-gray-900">{formatLogPeriod(log)}</span>
                        </div>
                      </div>
                      {log.errorMessage && (
                        <p className="text-xs text-red-500 mt-2">{log.errorMessage}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
